import { buildRailTones, buildTones } from './buildTones';
import type { Theme } from './tokens';

/**
 * The scheme library.
 *
 * Every scheme keeps three things fixed, because they ARE the product's
 * identity rather than decoration:
 *   - Paper content surfaces, one dark nav rail. The rail is the only large
 *     dark field; content is always calm.
 *   - The cherry stays a cherry. Its hue is allowed to move a few degrees to
 *     sit in the theme's family, never far enough to stop being the logo.
 *   - Pill shape language (radius), unchanged.
 *
 * What varies is the ground's temperature, the rail's hue, and which color
 * carries primary. The logo's two colors — cherry (warm rose, ~15deg) and stem
 * (lilac, ~300deg) — are the anchors every scheme is chosen against: a scheme
 * either sits IN the cherry's family (Sorbet, Espresso), sits in the STEM's
 * family (Plum Rail), or sits opposite so the cherry reads as the accent
 * (Marine, Grove, Graphite).
 */

// ---------------------------------------------------------------------------
// Plum Rail — the current look, now expressed as data.
// ---------------------------------------------------------------------------
const plumRail: Theme = {
  id: 'plum-rail',
  name: 'Plum Rail',
  description: 'Porcelain paper, one deep plum rail, orchid accent. The stem colour leads.',
  mode: 'light',
  chromeColor: '#2f1e3a',
  core: {
    background: 'oklch(0.966 0.010 310)',
    foreground: 'oklch(0.32 0.045 310)',
    card: 'oklch(0.995 0.002 310)',
    cardForeground: 'oklch(0.32 0.045 310)',
    popover: 'oklch(0.995 0.002 310)',
    popoverForeground: 'oklch(0.32 0.045 310)',
    primary: 'oklch(0.50 0.155 300)',
    primaryForeground: 'oklch(0.99 0 0)',
    secondary: 'oklch(0.936 0.018 305)',
    secondaryForeground: 'oklch(0.32 0.045 310)',
    muted: 'oklch(0.945 0.012 308)',
    mutedForeground: 'oklch(0.575 0.045 305)',
    accent: 'oklch(0.945 0.015 305)',
    accentForeground: 'oklch(0.32 0.045 310)',
    destructive: 'oklch(0.65 0.22 25)',
    destructiveForeground: 'oklch(0.99 0 0)',
    border: 'oklch(0.905 0.018 308)',
    input: 'oklch(0.905 0.018 308)',
    ring: 'oklch(0.50 0.155 300)',
    sidebar: 'oklch(0.27 0.055 310)',
    sidebarForeground: 'oklch(0.85 0.045 305)',
    sidebarPrimary: 'oklch(0.74 0.115 300)',
    sidebarPrimaryForeground: 'oklch(0.22 0.05 310)',
    sidebarAccent: 'oklch(0.35 0.07 305)',
    sidebarAccentForeground: 'oklch(0.97 0.01 305)',
    sidebarBorder: 'oklch(0.35 0.06 308)',
    sidebarRing: 'oklch(0.74 0.115 300)',
    brandCherry: 'oklch(0.44 0.15 15)',
    brandCherryOnRail: 'oklch(0.68 0.16 15)',
    brandStem: 'oklch(0.72 0.115 300)',
    chart: [
      'oklch(0.50 0.155 300)',
      'oklch(0.68 0.07 160)',
      'oklch(0.50 0.13 245)',
      'oklch(0.72 0.11 75)',
      'oklch(0.62 0.09 340)',
    ],
  },
  tones: buildTones({ mode: 'light', neutralHue: 310, neutralChroma: 0.022 }),
  railTones: buildRailTones({ mode: 'light', railL: 0.27, railHue: 310, neutralHue: 308, neutralChroma: 0.03 }),
};

// ---------------------------------------------------------------------------
// Sorbet — the cherry leads. Warmest scheme; most literally "sundae".
// ---------------------------------------------------------------------------
const sorbet: Theme = {
  id: 'sorbet',
  name: 'Sorbet',
  description: 'Warm cream paper, mulled-berry rail, the logo cherry as primary.',
  mode: 'light',
  chromeColor: '#3b1f2b',
  core: {
    background: 'oklch(0.972 0.014 45)',
    foreground: 'oklch(0.31 0.045 20)',
    card: 'oklch(0.995 0.005 45)',
    cardForeground: 'oklch(0.31 0.045 20)',
    popover: 'oklch(0.995 0.005 45)',
    popoverForeground: 'oklch(0.31 0.045 20)',
    primary: 'oklch(0.525 0.165 15)',
    primaryForeground: 'oklch(0.99 0 0)',
    secondary: 'oklch(0.940 0.024 40)',
    secondaryForeground: 'oklch(0.31 0.045 20)',
    muted: 'oklch(0.950 0.016 42)',
    mutedForeground: 'oklch(0.570 0.042 25)',
    accent: 'oklch(0.948 0.020 40)',
    accentForeground: 'oklch(0.31 0.045 20)',
    destructive: 'oklch(0.62 0.215 28)',
    destructiveForeground: 'oklch(0.99 0 0)',
    border: 'oklch(0.906 0.022 38)',
    input: 'oklch(0.906 0.022 38)',
    ring: 'oklch(0.525 0.165 15)',
    sidebar: 'oklch(0.275 0.062 18)',
    sidebarForeground: 'oklch(0.865 0.038 30)',
    sidebarPrimary: 'oklch(0.755 0.125 20)',
    sidebarPrimaryForeground: 'oklch(0.22 0.055 18)',
    sidebarAccent: 'oklch(0.355 0.072 18)',
    sidebarAccentForeground: 'oklch(0.972 0.012 30)',
    sidebarBorder: 'oklch(0.355 0.062 20)',
    sidebarRing: 'oklch(0.755 0.125 20)',
    brandCherry: 'oklch(0.50 0.175 12)',
    brandCherryOnRail: 'oklch(0.735 0.155 14)',
    brandStem: 'oklch(0.70 0.125 302)',
    chart: [
      'oklch(0.525 0.165 15)',
      'oklch(0.70 0.115 302)',
      'oklch(0.66 0.115 48)',
      'oklch(0.62 0.105 165)',
      'oklch(0.58 0.115 265)',
    ],
  },
  tones: buildTones({ mode: 'light', chroma: 1.06, hueShift: 3, neutralHue: 32, neutralChroma: 0.024 }),
  railTones: buildRailTones({ mode: 'light', chroma: 1.06, hueShift: 3, railL: 0.275, railHue: 18, neutralHue: 24, neutralChroma: 0.032 }),
};

// ---------------------------------------------------------------------------
// Espresso — warm neutral ground, dark roast rail. Cherry reads as fruit on
// coffee: same warm family, so nothing fights.
// ---------------------------------------------------------------------------
const espresso: Theme = {
  id: 'espresso',
  name: 'Espresso',
  description: 'Oat-milk paper, dark roast rail, burnt-amber accent. Warm and quiet.',
  mode: 'light',
  chromeColor: '#2b211a',
  core: {
    background: 'oklch(0.968 0.011 78)',
    foreground: 'oklch(0.30 0.028 62)',
    card: 'oklch(0.995 0.004 78)',
    cardForeground: 'oklch(0.30 0.028 62)',
    popover: 'oklch(0.995 0.004 78)',
    popoverForeground: 'oklch(0.30 0.028 62)',
    primary: 'oklch(0.500 0.125 52)',
    primaryForeground: 'oklch(0.99 0 0)',
    secondary: 'oklch(0.936 0.020 76)',
    secondaryForeground: 'oklch(0.30 0.028 62)',
    muted: 'oklch(0.946 0.014 76)',
    mutedForeground: 'oklch(0.560 0.030 65)',
    accent: 'oklch(0.944 0.018 74)',
    accentForeground: 'oklch(0.30 0.028 62)',
    destructive: 'oklch(0.60 0.205 28)',
    destructiveForeground: 'oklch(0.99 0 0)',
    border: 'oklch(0.900 0.020 74)',
    input: 'oklch(0.900 0.020 74)',
    ring: 'oklch(0.500 0.125 52)',
    sidebar: 'oklch(0.255 0.028 62)',
    sidebarForeground: 'oklch(0.855 0.024 72)',
    sidebarPrimary: 'oklch(0.760 0.105 62)',
    sidebarPrimaryForeground: 'oklch(0.21 0.028 62)',
    sidebarAccent: 'oklch(0.338 0.032 62)',
    sidebarAccentForeground: 'oklch(0.968 0.010 72)',
    sidebarBorder: 'oklch(0.340 0.028 64)',
    sidebarRing: 'oklch(0.760 0.105 62)',
    brandCherry: 'oklch(0.47 0.165 14)',
    brandCherryOnRail: 'oklch(0.700 0.155 16)',
    brandStem: 'oklch(0.70 0.095 300)',
    chart: [
      'oklch(0.500 0.125 52)',
      'oklch(0.60 0.105 160)',
      'oklch(0.54 0.115 250)',
      'oklch(0.68 0.125 88)',
      'oklch(0.56 0.135 12)',
    ],
  },
  tones: buildTones({ mode: 'light', chroma: 0.92, neutralHue: 70, neutralChroma: 0.016 }),
  railTones: buildRailTones({ mode: 'light', chroma: 0.92, railL: 0.255, railHue: 62, neutralHue: 66, neutralChroma: 0.024 }),
};

// ---------------------------------------------------------------------------
// Marine — cool ground, deep navy rail. The cherry becomes a true accent:
// warm against cold is the strongest the logo ever looks.
// ---------------------------------------------------------------------------
const marine: Theme = {
  id: 'marine',
  name: 'Marine',
  description: 'Cool porcelain, deep navy rail, teal accent. The cherry pops hardest here.',
  mode: 'light',
  chromeColor: '#0e2440',
  core: {
    background: 'oklch(0.966 0.008 240)',
    foreground: 'oklch(0.30 0.038 248)',
    card: 'oklch(0.995 0.003 240)',
    cardForeground: 'oklch(0.30 0.038 248)',
    popover: 'oklch(0.995 0.003 240)',
    popoverForeground: 'oklch(0.30 0.038 248)',
    primary: 'oklch(0.505 0.115 218)',
    primaryForeground: 'oklch(0.99 0 0)',
    secondary: 'oklch(0.934 0.016 236)',
    secondaryForeground: 'oklch(0.30 0.038 248)',
    muted: 'oklch(0.944 0.011 238)',
    mutedForeground: 'oklch(0.565 0.034 242)',
    accent: 'oklch(0.943 0.014 236)',
    accentForeground: 'oklch(0.30 0.038 248)',
    destructive: 'oklch(0.62 0.215 25)',
    destructiveForeground: 'oklch(0.99 0 0)',
    border: 'oklch(0.902 0.016 238)',
    input: 'oklch(0.902 0.016 238)',
    ring: 'oklch(0.505 0.115 218)',
    sidebar: 'oklch(0.248 0.064 248)',
    sidebarForeground: 'oklch(0.852 0.036 232)',
    sidebarPrimary: 'oklch(0.755 0.098 210)',
    sidebarPrimaryForeground: 'oklch(0.20 0.045 250)',
    sidebarAccent: 'oklch(0.334 0.072 247)',
    sidebarAccentForeground: 'oklch(0.968 0.010 232)',
    sidebarBorder: 'oklch(0.336 0.066 247)',
    sidebarRing: 'oklch(0.755 0.098 210)',
    brandCherry: 'oklch(0.475 0.175 16)',
    brandCherryOnRail: 'oklch(0.712 0.165 16)',
    brandStem: 'oklch(0.72 0.110 298)',
    chart: [
      'oklch(0.505 0.115 218)',
      'oklch(0.60 0.155 18)',
      'oklch(0.60 0.115 160)',
      'oklch(0.66 0.125 82)',
      'oklch(0.56 0.145 300)',
    ],
  },
  tones: buildTones({ mode: 'light', chroma: 0.97, neutralHue: 240, neutralChroma: 0.018 }),
  railTones: buildRailTones({ mode: 'light', chroma: 0.97, railL: 0.248, railHue: 248, neutralHue: 244, neutralChroma: 0.034 }),
};

// ---------------------------------------------------------------------------
// Harbour — the working blue. Marine leans teal (hue 218); this sits at true
// blue (255), where most software you have open all day already lives. Kept
// deliberately quieter than Marine so the two read as different jobs rather
// than two takes on the same idea.
// ---------------------------------------------------------------------------
const harbour: Theme = {
  id: 'harbour',
  name: 'Harbour',
  description: 'Cool grey paper, slate-navy rail, steel blue accent. The quiet workday blue.',
  mode: 'light',
  chromeColor: '#3b4149',
  core: {
    background: 'oklch(0.967 0.004 248)',
    foreground: 'oklch(0.300 0.032 252)',
    card: 'oklch(0.997 0.002 248)',
    cardForeground: 'oklch(0.300 0.032 252)',
    popover: 'oklch(0.996 0.003 250)',
    popoverForeground: 'oklch(0.300 0.032 252)',
    primary: 'oklch(0.498 0.122 250)',
    primaryForeground: 'oklch(0.99 0 0)',
    secondary: 'oklch(0.934 0.014 248)',
    secondaryForeground: 'oklch(0.300 0.032 252)',
    muted: 'oklch(0.944 0.010 250)',
    mutedForeground: 'oklch(0.562 0.030 250)',
    accent: 'oklch(0.943 0.013 248)',
    accentForeground: 'oklch(0.300 0.032 252)',
    destructive: 'oklch(0.620 0.215 25)',
    destructiveForeground: 'oklch(0.99 0 0)',
    border: 'oklch(0.902 0.014 248)',
    input: 'oklch(0.902 0.014 248)',
    ring: 'oklch(0.498 0.122 250)',
    sidebar: 'oklch(0.302 0.020 250)',
    sidebarForeground: 'oklch(0.868 0.014 250)',
    sidebarPrimary: 'oklch(0.800 0.070 248)',
    sidebarPrimaryForeground: 'oklch(0.240 0.020 250)',
    sidebarAccent: 'oklch(0.382 0.026 250)',
    sidebarAccentForeground: 'oklch(0.972 0.006 250)',
    sidebarBorder: 'oklch(0.384 0.024 250)',
    sidebarRing: 'oklch(0.800 0.070 248)',
    brandCherry: 'oklch(0.472 0.178 15)',
    brandCherryOnRail: 'oklch(0.715 0.165 15)',
    brandStem: 'oklch(0.720 0.110 299)',
    chart: [
      'oklch(0.498 0.122 250)',
      'oklch(0.590 0.155 17)',
      'oklch(0.600 0.110 162)',
      'oklch(0.665 0.125 82)',
      'oklch(0.575 0.140 302)',
    ],
  },
  tones: buildTones({ mode: 'light', chroma: 0.96, neutralHue: 250, neutralChroma: 0.016 }),
  railTones: buildRailTones({ mode: 'light', chroma: 0.96, railL: 0.302, railHue: 250, neutralHue: 250, neutralChroma: 0.020 }),
};

// ---------------------------------------------------------------------------
// Iris — blue on its way to the logo's stem. At hue 277 it is still read as
// blue, but it is the closest any light scheme gets to the lilac in the mark,
// which makes the stem look chosen rather than inherited.
// ---------------------------------------------------------------------------
const iris: Theme = {
  id: 'iris',
  name: 'Iris',
  description: 'Blue-lilac paper, deep indigo rail, periwinkle accent. Softest of the blues.',
  mode: 'light',
  chromeColor: '#2b2450',
  core: {
    background: 'oklch(0.968 0.010 275)',
    foreground: 'oklch(0.310 0.040 278)',
    card: 'oklch(0.996 0.003 275)',
    cardForeground: 'oklch(0.310 0.040 278)',
    popover: 'oklch(0.996 0.003 275)',
    popoverForeground: 'oklch(0.310 0.040 278)',
    primary: 'oklch(0.505 0.170 277)',
    primaryForeground: 'oklch(0.99 0 0)',
    secondary: 'oklch(0.936 0.020 274)',
    secondaryForeground: 'oklch(0.310 0.040 278)',
    muted: 'oklch(0.946 0.013 275)',
    mutedForeground: 'oklch(0.570 0.040 275)',
    accent: 'oklch(0.945 0.017 274)',
    accentForeground: 'oklch(0.310 0.040 278)',
    destructive: 'oklch(0.630 0.215 25)',
    destructiveForeground: 'oklch(0.99 0 0)',
    border: 'oklch(0.904 0.018 274)',
    input: 'oklch(0.904 0.018 274)',
    ring: 'oklch(0.505 0.170 277)',
    sidebar: 'oklch(0.262 0.058 280)',
    sidebarForeground: 'oklch(0.855 0.040 274)',
    sidebarPrimary: 'oklch(0.758 0.115 277)',
    sidebarPrimaryForeground: 'oklch(0.210 0.050 280)',
    sidebarAccent: 'oklch(0.345 0.068 278)',
    sidebarAccentForeground: 'oklch(0.970 0.010 274)',
    sidebarBorder: 'oklch(0.348 0.060 278)',
    sidebarRing: 'oklch(0.758 0.115 277)',
    brandCherry: 'oklch(0.462 0.178 14)',
    brandCherryOnRail: 'oklch(0.708 0.166 14)',
    brandStem: 'oklch(0.725 0.115 300)',
    chart: [
      'oklch(0.505 0.170 277)',
      'oklch(0.595 0.150 17)',
      'oklch(0.605 0.110 163)',
      'oklch(0.670 0.125 82)',
      'oklch(0.590 0.135 200)',
    ],
  },
  tones: buildTones({ mode: 'light', neutralHue: 276, neutralChroma: 0.018 }),
  railTones: buildRailTones({ mode: 'light', railL: 0.262, railHue: 280, neutralHue: 278, neutralChroma: 0.030 }),
};

// ---------------------------------------------------------------------------
// Grove — the "Pistachio" half of the original design language, taken
// seriously. Cherries grow on trees; red on green is the oldest pairing there
// is, and the muted chroma keeps it from reading as Christmas.
// ---------------------------------------------------------------------------
const grove: Theme = {
  id: 'grove',
  name: 'Grove',
  description: 'Pistachio paper, forest rail, moss accent. Cherry-on-green, dialled down.',
  mode: 'light',
  chromeColor: '#1e2c22',
  core: {
    background: 'oklch(0.968 0.012 138)',
    foreground: 'oklch(0.29 0.032 150)',
    card: 'oklch(0.995 0.004 138)',
    cardForeground: 'oklch(0.29 0.032 150)',
    popover: 'oklch(0.995 0.004 138)',
    popoverForeground: 'oklch(0.29 0.032 150)',
    primary: 'oklch(0.475 0.105 150)',
    primaryForeground: 'oklch(0.99 0 0)',
    secondary: 'oklch(0.934 0.022 136)',
    secondaryForeground: 'oklch(0.29 0.032 150)',
    muted: 'oklch(0.946 0.015 138)',
    mutedForeground: 'oklch(0.555 0.032 148)',
    accent: 'oklch(0.944 0.019 136)',
    accentForeground: 'oklch(0.29 0.032 150)',
    destructive: 'oklch(0.60 0.210 26)',
    destructiveForeground: 'oklch(0.99 0 0)',
    border: 'oklch(0.900 0.022 136)',
    input: 'oklch(0.900 0.022 136)',
    ring: 'oklch(0.475 0.105 150)',
    sidebar: 'oklch(0.252 0.034 152)',
    sidebarForeground: 'oklch(0.852 0.030 138)',
    sidebarPrimary: 'oklch(0.755 0.105 138)',
    sidebarPrimaryForeground: 'oklch(0.20 0.032 152)',
    sidebarAccent: 'oklch(0.335 0.040 150)',
    sidebarAccentForeground: 'oklch(0.968 0.010 138)',
    sidebarBorder: 'oklch(0.338 0.036 150)',
    sidebarRing: 'oklch(0.755 0.105 138)',
    brandCherry: 'oklch(0.472 0.180 15)',
    brandCherryOnRail: 'oklch(0.708 0.168 15)',
    brandStem: 'oklch(0.70 0.105 300)',
    chart: [
      'oklch(0.475 0.105 150)',
      'oklch(0.58 0.155 16)',
      'oklch(0.66 0.125 85)',
      'oklch(0.54 0.125 252)',
      'oklch(0.58 0.135 302)',
    ],
  },
  tones: buildTones({ mode: 'light', chroma: 0.95, neutralHue: 140, neutralChroma: 0.018 }),
  railTones: buildRailTones({ mode: 'light', chroma: 0.95, railL: 0.252, railHue: 152, neutralHue: 146, neutralChroma: 0.026 }),
};

// ---------------------------------------------------------------------------
// Graphite — maximum restraint. Near-neutral everything, so the only colour
// moments in the whole UI are status tones and the cherry itself.
// ---------------------------------------------------------------------------
const graphite: Theme = {
  id: 'graphite',
  name: 'Graphite',
  description: 'Neutral paper, near-black rail, cherry as the only brand colour.',
  mode: 'light',
  chromeColor: '#1c1b1d',
  core: {
    background: 'oklch(0.968 0.003 300)',
    foreground: 'oklch(0.28 0.008 300)',
    card: 'oklch(0.998 0.001 300)',
    cardForeground: 'oklch(0.28 0.008 300)',
    popover: 'oklch(0.998 0.001 300)',
    popoverForeground: 'oklch(0.28 0.008 300)',
    primary: 'oklch(0.475 0.155 14)',
    primaryForeground: 'oklch(0.99 0 0)',
    secondary: 'oklch(0.932 0.005 300)',
    secondaryForeground: 'oklch(0.28 0.008 300)',
    muted: 'oklch(0.944 0.004 300)',
    mutedForeground: 'oklch(0.552 0.010 300)',
    accent: 'oklch(0.942 0.005 300)',
    accentForeground: 'oklch(0.28 0.008 300)',
    destructive: 'oklch(0.60 0.210 26)',
    destructiveForeground: 'oklch(0.99 0 0)',
    border: 'oklch(0.898 0.006 300)',
    input: 'oklch(0.898 0.006 300)',
    ring: 'oklch(0.475 0.155 14)',
    sidebar: 'oklch(0.232 0.008 300)',
    sidebarForeground: 'oklch(0.848 0.006 300)',
    sidebarPrimary: 'oklch(0.735 0.135 16)',
    sidebarPrimaryForeground: 'oklch(0.19 0.008 300)',
    sidebarAccent: 'oklch(0.318 0.010 300)',
    sidebarAccentForeground: 'oklch(0.968 0.004 300)',
    sidebarBorder: 'oklch(0.322 0.009 300)',
    sidebarRing: 'oklch(0.735 0.135 16)',
    brandCherry: 'oklch(0.475 0.175 14)',
    brandCherryOnRail: 'oklch(0.715 0.165 14)',
    brandStem: 'oklch(0.70 0.100 300)',
    chart: [
      'oklch(0.475 0.155 14)',
      'oklch(0.56 0.100 250)',
      'oklch(0.60 0.100 160)',
      'oklch(0.68 0.115 82)',
      'oklch(0.56 0.120 302)',
    ],
  },
  tones: buildTones({ mode: 'light', chroma: 0.9, neutralHue: 300, neutralChroma: 0.008 }),
  railTones: buildRailTones({ mode: 'light', chroma: 0.9, railL: 0.232, railHue: 300, neutralHue: 300, neutralChroma: 0.012 }),
};

// ---------------------------------------------------------------------------
// Midnight — the one scheme where content surfaces go dark too. The rail stops
// being the only dark field, so it separates by going DARKER than the content
// rather than lighter, keeping the same figure/ground relationship.
// ---------------------------------------------------------------------------
const midnight: Theme = {
  id: 'midnight',
  name: 'Midnight',
  description: 'Softened dark. Warm ink-plum content, blacker rail, orchid accent.',
  mode: 'dark',
  chromeColor: '#1b1620',
  core: {
    // Content sits well above black — closer to a dimmed page than a lights-out
    // one — while the rail stays the darkest field on screen, so the same
    // figure/ground relationship as the light schemes survives.
    background: 'oklch(0.255 0.016 302)',
    foreground: 'oklch(0.905 0.012 302)',
    card: 'oklch(0.298 0.020 302)',
    cardForeground: 'oklch(0.905 0.012 302)',
    popover: 'oklch(0.318 0.022 302)',
    popoverForeground: 'oklch(0.928 0.012 302)',
    primary: 'oklch(0.755 0.135 300)',
    primaryForeground: 'oklch(0.220 0.035 302)',
    secondary: 'oklch(0.360 0.024 302)',
    secondaryForeground: 'oklch(0.905 0.012 302)',
    muted: 'oklch(0.330 0.020 302)',
    mutedForeground: 'oklch(0.702 0.024 302)',
    accent: 'oklch(0.360 0.026 302)',
    accentForeground: 'oklch(0.935 0.012 302)',
    destructive: 'oklch(0.658 0.195 25)',
    destructiveForeground: 'oklch(0.185 0.030 25)',
    border: 'oklch(0.382 0.022 302)',
    input: 'oklch(0.382 0.022 302)',
    ring: 'oklch(0.755 0.135 300)',
    sidebar: 'oklch(0.198 0.016 303)',
    sidebarForeground: 'oklch(0.812 0.020 302)',
    sidebarPrimary: 'oklch(0.778 0.125 300)',
    sidebarPrimaryForeground: 'oklch(0.200 0.028 303)',
    sidebarAccent: 'oklch(0.298 0.026 302)',
    sidebarAccentForeground: 'oklch(0.935 0.012 302)',
    sidebarBorder: 'oklch(0.322 0.024 302)',
    sidebarRing: 'oklch(0.778 0.125 300)',
    brandCherry: 'oklch(0.715 0.165 14)',
    brandCherryOnRail: 'oklch(0.720 0.168 14)',
    brandStem: 'oklch(0.760 0.115 300)',
    chart: [
      'oklch(0.755 0.135 300)',
      'oklch(0.715 0.115 165)',
      'oklch(0.705 0.125 250)',
      'oklch(0.790 0.125 82)',
      'oklch(0.730 0.145 16)',
    ],
  },
  // groundL sits between the background and the card, so chip washes clear the
  // card they sit on without floating off it.
  tones: buildTones({ mode: 'dark', groundL: 0.258, neutralHue: 302, neutralChroma: 0.022 }),
  // The rail is DARKER than the content here, so its washes are placed against
  // the rail's own lightness rather than the content's.
  railTones: buildRailTones({ mode: 'dark', railL: 0.198, railHue: 303, neutralHue: 302, neutralChroma: 0.026 }),
};

export const THEMES: Theme[] = [plumRail, sorbet, espresso, marine, harbour, iris, grove, graphite, midnight];

export const DEFAULT_THEME_ID = plumRail.id;

export function getTheme(id: string | null | undefined): Theme {
  return THEMES.find((t) => t.id === id) ?? plumRail;
}
